import Cookie from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import { create } from 'zustand'
import { LOCAL_STORAGE_KEYS } from '../../../constants/local-storage-keys'
import { IDToken } from '../../../types/token'
import { UserAccount } from '../../../types/user'
import { localStorageManager } from '../../../utils/local-storage'

export type AuthContext = Partial<{
  username: string
  race: string
  race_date: string
  image: string
  distance: string
  redirect_to: string
  type: string
}>

export type InitAuthSession = {
  id: string
  username: string
  otpMedium: 'EMAIL' | 'SMS'
  preview: string
  redirectUri?: string
}

export type AuthSession = {
  refreshToken: string
  idToken: string
  accessToken: string
  idTokenDecoded: IDToken
}

type AuthStore = {
  initSession?: InitAuthSession
  session?: AuthSession
  isAuthenticated: boolean
  redirectUri?: string
  context?: AuthContext | Record<string, string>
  user?: UserAccount
  fetchUserLoading: boolean
  setUser: (user?: UserAccount) => void
  setFetchUserLoading: (loading: boolean) => void
  loadSession: () => void
  resetInitSession: () => void
  setInitSession: (s: InitAuthSession) => void
  resetSession: () => void
  setRedirectUri: (uri: string) => void
}

const getIdTokenFromQueryParam = (): string | undefined => {
  if (typeof window === 'undefined') return undefined
  return new URLSearchParams(window.location.search).get('idToken') ?? undefined
}

const generateSession = (): AuthSession | undefined => {
  const idTokenFromParam = getIdTokenFromQueryParam()

  if (idTokenFromParam) {
    return {
      idToken: idTokenFromParam,
      refreshToken: '',
      accessToken: '',
      idTokenDecoded: jwtDecode(idTokenFromParam),
    }
  }

  const idToken = Cookie.get(import.meta.env.VITE_ID_COOKIE)
  const refreshToken = Cookie.get(import.meta.env.VITE_REFRESH_COOKIE)
  const accessToken = Cookie.get(import.meta.env.VITE_ACCESS_COOKIE)

  if (idToken && refreshToken && accessToken) {
    const s: AuthSession = {
      idToken,
      refreshToken,
      accessToken,
      idTokenDecoded: jwtDecode(idToken),
    }

    return s
  }
}

const padBase64 = (value: string) => value.padEnd(Math.ceil(value.length / 4) * 4, '=')

const decodeContextFromQueryParams = (): AuthContext | Record<string, string> | undefined => {
  if (typeof window === 'undefined') return undefined

  try {
    const params = new URLSearchParams(window.location.search)
    const contextParam = params.get('context')

    if (!contextParam) return undefined

    const normalized = padBase64(contextParam.replace(/-/g, '+').replace(/_/g, '/'))
    const decoded = atob(normalized)
    const bytes = Uint8Array.from(decoded, (c) => c.charCodeAt(0))

    return JSON.parse(new TextDecoder().decode(bytes))
  } catch (error) {
    console.error('Failed to decode context from query params:', error)
    return undefined
  }
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  initSession: localStorageManager.get(LOCAL_STORAGE_KEYS.initSession),
  session: generateSession(),
  isAuthenticated: !!generateSession(),
  redirectUri: sessionStorage.getItem(LOCAL_STORAGE_KEYS.redirectUri) || undefined,
  context: decodeContextFromQueryParams(),
  user: undefined,
  fetchUserLoading: true,
  setUser: (user) => set({ user }),
  setFetchUserLoading: (fetchUserLoading) => set({ fetchUserLoading }),
  setInitSession: (initSession: InitAuthSession) => {
    set({ initSession, redirectUri: get().redirectUri ?? initSession?.redirectUri })
    localStorageManager.set(LOCAL_STORAGE_KEYS.initSession, initSession)
    if (initSession.redirectUri) {
      sessionStorage.setItem(LOCAL_STORAGE_KEYS.redirectUri, initSession.redirectUri)
    }
  },
  resetInitSession: () => {
    set({ initSession: undefined })
    localStorageManager.remove(LOCAL_STORAGE_KEYS.initSession)
    sessionStorage.removeItem(LOCAL_STORAGE_KEYS.redirectUri)
  },
  loadSession: () => {
    const session = getIdTokenFromQueryParam() ? get().session : generateSession()
    set({
      session,
      isAuthenticated: !!session,
    })
  },
  resetSession: () => {
    set({ session: undefined, isAuthenticated: false })
  },
  setRedirectUri: (redirectUri: string) => {
    if (redirectUri) {
      sessionStorage.setItem(LOCAL_STORAGE_KEYS.redirectUri, redirectUri)
      set({ redirectUri })
    }
  },
}))
